<template>
    <div>
        <div
            class="relative flex flex-row w-full items-center align-baseline transition-all ease-in-out duration-1000"
            :class="{ 'bg-blue-20 shadow-3dp' : isSelecting }"
        >
            <product-field-info
                :field="field"
                :isSelecting="isSelecting"
                :selected="selected"
                :selectedDisplayName="formatSelectedName"
                :position="position"
                :total="total"
                :isMobileView="isMobileView"
                :disabled="disabled"
                :showDescription="showSideBarDescription"
                :sizes="getSizes"
                @ShowLearMore="ShowLearMore"
                @HideLearMore="HideLearMore"
            />

            <span class="configurator-field-line" v-show="!isSelecting && !selected"></span>

            <div class="hidden lg:flex grid-cols-3 w-full order-3 mr-4 | md:order-2 md:w-auto md:flex-1" :class="{'mt-6': isSelecting}">
                <div class="flex gap-2 items-center">
                    <div class="flex flex-col w-full">
                        <SelectInput :options="[{label: 'mm'}, {label: 'cm'}, {label: 'in'}]"
                                     :value="getUnitValue(sizes.unit)"
                                     @change="changedUnit"
                                     :disabled="disabled"
                                     placeholder="Unit type"
                                     button-class="max-h-[36px]"
                                     :class="{'selecting': isSelecting, 'selected': selected}"
                        />
                    </div>
                    <div class="relative flex flex-col w-full">
                        <span v-if="showError" class="text-5 text-5--regular !text-red absolute -top-full">Product size can`t be larger than 4m<sup>2</sup></span>
                        <NumberInput placeholder="Width"
                                     :disabled="disabled"
                                     :value="sizes.width"
                                     min="1"
                                     @change="value => width = setValue(value)"
                                     :class="{'selecting': isSelecting, 'selected': selected}"
                        />
                    </div>
                    <div class="relative flex flex-col w-full">
                        <span v-if="showError" class="text-5 text-5--regular !text-red absolute -top-full">Product size can`t be larger than 4m<sup>2</sup></span>
                        <NumberInput placeholder="Height"
                                     :disabled="disabled"
                                     :value="sizes.height"
                                     min="1"
                                     @change="value => height = setValue(value)"
                                     :class="{'selecting': isSelecting, 'selected': selected}"

                        />
                    </div>

                    <div class="flex flex-col ml-0.5 min-w-[61px]">
                        <div
                            class="btn-info block mb-1.5 max-w-fit last:mb-0"
                            role="button"
                            @click="ShowLearMore"
                        >
                            <icon-plus/>
                            <span>Info</span>
                        </div>

                        <a v-if="field.video" :href="field.video" class="btn-info block">
                            <icon-play/>
                            <span>Video</span>
                        </a>
                    </div>
                </div>
            </div>

            <div class="flex flex-col items-end justify-center w-full mr-[20px] lg:hidden">
                <div class="flex flex-col" v-if="disabled">
                    <div
                        v-if="hasDescriptions"
                        class="btn-info ml-2 mb-2 last:mb-0"
                        role="button"
                        @click="ShowLearMore"
                    >
                        <icon-plus class=""/>
                        <span>Info</span>
                    </div>

                    <a v-if="field.video" target="_blank" :href="field.video" class="btn-info block">
                        <icon-play/>
                        <span>Video</span>
                    </a>
                </div>
                
                <div v-show="!disabled" class="mt-8">
                    <select-button :clicked="showMobileSelect" :selected="selected"></select-button>
                </div>
            </div>

            <MountingPortal append mountTo="#configurator-field-menu" multiple>
                <MobileSelect
                    :show="mobileSelect" @close="hideMobileSelect"
                    :title="field.label + ' options'"
                    background="bg-blue-20"
                >
                    <div class="flex flex-col items-stretch pt-4 overflow-y-auto h-[calc(100%-140px)]">
                        <div class="grid grid-cols-1 px-5 py-3">
                            <div class="relative flex flex-col w-full mb-6">
                                <label>
                                    <span class="label-text">Select a unit type</span>
                                    <button @click="openRadioSelect = true" class="mobile-input mobile-input-select w-full">
                                        <span>{{ sizes.unit ? sizes.unit : 'Unit type' }}</span>
                                        <drop-down :class="{'[&_path]:!fill-darkblue-200': disabled}"/>
                                    </button>
                                </label>
                                
                                <RadioSelectPopup :show="openRadioSelect" @close="hiddenRadioSelect" title="Unit type" :options="[{'label': 'mm', 'value': 'mm'}, {'label': 'cm', 'value': 'cm'}, {'label': 'in', 'value': 'in'}]"/>
                            </div>
                            
                            <div class="flex flex-col w-full mb-8">
                                <label>
                                    <span class="label-text">Overall width</span>
                                    <input
                                        class="mobile-input mobile-input-text w-full"
                                        type="number"
                                        :value="mobileSelectOption.width"
                                        :disabled="disabled"
                                        placeholder="Overall width"
                                        @change="e => mobileSelectOption.width = setValue(e.target.value)"
                                    />
                                </label>
                                <span v-if="showError" class="inline-block mt-1.5 text-4 !text-red">Product size can`t be larger than 4m<sup>2</sup></span>
                            </div>
                            
                            <div class="flex flex-col w-full">
                                <span class="label-text">Overall height</span>
                                <input
                                    class="mobile-input mobile-input-text w-full"
                                    type="number"
                                    :value="mobileSelectOption.height"
                                    :disabled="disabled"
                                    placeholder="Overall height"
                                    @change="e => mobileSelectOption.height = setValue(e.target.value)"
                                />
                            </div>
                            <span v-if="showError" class="inline-block mt-1.5 text-4 !text-red">Product size can`t be larger than 4m<sup>2</sup></span>
                        </div>
                    </div>

                    <div class="px-5">
                        <button
                            class="btn btn--primary btn--big w-full"
                            :class="{ '': allMobileFieldsSelected(), 'btn--disable' : !allMobileFieldsSelected()  }"
                            :disabled="!allMobileFieldsSelected"
                            @click="selectMobileOption()"
                        >
                            <span>Confirm Inputs</span>
                        </button>
                    </div>

                </MobileSelect>
            </MountingPortal>
        </div>
    </div>
</template>

<script lang="js">
import SlideUpDown from "vue-slide-up-down"
import ProductFieldOption from "../../../_models/ProductFieldOption"
import FiledOptionsDescriptions from "../FiledOptionsDescriptions"
import IconConfiguratorCheck from "../Icons/IconConfiguratorCheck"
import IconInfo from "../Icons/IconInfo"
import ProductFieldInfo from "../helpers/ProductFieldInfo";
import SelectButton from "../helpers/SelectButton";
import MobileSelect from "../MobileSelect";
import IconPlus from "../Icons/IconPlus.vue";
import DropDown from "../../../../../assets/js/vue/icons/drop-down.vue";
import RadioSelectPopup from "../RadioSelectPopup.vue";
import SelectInput from "../SelectInput.vue";
import NumberInput from "../NumberInput.vue";
import IconPlay from "../Icons/IconPlay.vue";

const defaultSelectOptionObj  = {
    unit: '',
    width: '',
    height: '',
}

export default {
    name: "FieldSize",
    components: {
        IconPlay,
        NumberInput,
        SelectInput,
        RadioSelectPopup,
        DropDown,
        IconPlus, MobileSelect, SelectButton, ProductFieldInfo, IconInfo, IconConfiguratorCheck, SlideUpDown, FiledOptionsDescriptions },
    props: {
        name: '',
        field: {},
        sizes: {},
        total: 0,
        position: 0,
        selected: {
            default: null
        },
        disabled: {
            default: true
        },
        fieldUpdated: {
            type: Function,
            default: () => {}
        },
        isCircleSelected:{
            type: Boolean
        }
    },
    mounted(){
        this.height = this.sizes.height;
        this.width = this.sizes.width;
    },
    created() {
        this.onResize();
        window.addEventListener("resize", this.onResize);
    },
    destroyed() {
        window.removeEventListener("resize", this.onResize);
    },
    data() {
        return {
            height: this.sizes.height,
            width: this.sizes.width,
            unit: this.sizes.unit,
            mobileSelect: false,
            mobileSelectOption: defaultSelectOptionObj,
            showSideBarDescription: false,
            selecting: this.selected,
            selectedIndex: null,
            innerWidth: window.innerWidth,
            isMobileView: false,
            openRadioSelect: false,
        }
    },
    watch: {
        height() { this.updateHeight() },
        width() { this.updateWidth() },
        unit() { this.changed() },
        selected(newValue, oldValue) {
            console.log(newValue)
            if ( newValue !== oldValue ) {
                if ( newValue === null && !this.isSelecting ) {
                    this.sizes.width = null;
                    this.sizes.height = null;
                    this.width = null;
                    this.height = null;
                    this.unit = null;
                    this.sizes.unit = null;
                    this.mobileSelectOption.width;
                    this.mobileSelectOption.height;
                    this.mobileSelectOption.unit = null;
                }
            }
        }
    },
    computed:{
        canBeEdited(){
          return this.field.selectedOption !== null || this.isSelecting
        },
        isSelecting() {
            return this.showDescription;
        },
        showDescription(){
            return !this.disabled && !this.field.selectedOption
        },
        hasDescriptions() {
          return this.field.info ?? false;
            // return this.field.options?.filter(item => {
            //     return item.description.text !== '';
            // }).length ?? false;
        },
        formatSelectedName() {
            return this.selected?.label ?? '';
        },
        getSizes() {
          return this.sizes;  
        },
        showError() {
            const isTrue = (v) => {
                return v !== '' && v > 0;
            }
            return this.showDescription && ( (isTrue(this.width) && isTrue(this.height) && this.unit) || (isTrue(this.mobileSelectOption.width) && isTrue(this.mobileSelectOption.height) && this.mobileSelectOption.unit) ); 
        }
    },
    methods: {
        selectMobileOption() {
            
            this.unit = this.mobileSelectOption.unit;
            this.width = this.mobileSelectOption.width;
            this.height = this.mobileSelectOption.height;


            this.mobileSelectOption = defaultSelectOptionObj;
            this.hideMobileSelect()

            this.field.mapToForm = this.mapToForm();
            this.fieldUpdated();

        },
        showMobileSelect() {
           if(this.canBeEdited){
               this.mobileSelect = true;
               this.mobileSelectOption = defaultSelectOptionObj;
           }
        },
        hideMobileSelect() {
            this.mobileSelect = false;
            this.mobileSelectOption = defaultSelectOptionObj;
        },
        ShowLearMore(){
            this.showSideBarDescription = true;
        },
        HideLearMore(){
            this.showSideBarDescription = false;
        },
        setValue(value) {
            return value === '' ? '' : parseFloat(value)
        },
        allMobileFieldsSelected() {
            return this.mobileSelectOption.height && this.mobileSelectOption.width && this.mobileSelectOption.unit
        },
        allFieldsSelected() {
            return this.height && this.width && this.unit
        },
        updateWidth(){
            if(this.isCircleSelected){
                this.height = this.width;
            }
            this.changed();
        },
        updateHeight(){
            if(this.isCircleSelected){
                this.width = this.height;
            }
            this.changed();
        },
        changed() {
            this.field.selectedOption = null;
            this.field.mapToForm = this.mapToForm();

            if (this.allFieldsSelected()) {
                this.field.selectedOption = ProductFieldOption.fromPlain({
                    name: this.name,
                    price: 0
                });
            }

            this.fieldUpdated();
        },
        mapToForm() {
            return [
                {
                    id: 'height',
                    name: 'Height',
                    label: 'Height',
                    selected: {
                        name: this.height
                    }
                },
                {
                    id: 'width',
                    name: 'Width',
                    label: 'Width',
                    selected: {
                        name: this.width
                    }
                },
                {
                    id: 'unit',
                    name: 'Size Type',
                    label: 'Size Type',
                    selected: {
                        name: this.unit
                    }
                }
            ]

        },
        onResize(e) {
            this.innerWidth = window.innerWidth

            this.isMobileView = this.innerWidth < 1024;
        },
        hiddenRadioSelect(value) {
            this.openRadioSelect = false;
            this.sizes.unit = value;
            this.mobileSelectOption.unit = value;
        },
        getUnitValue(value){
            const sizes = {
                mm: 0,
                cm: 1,
                in: 2,
            }
          if ( value === '' ) return null; 
          return sizes[value];
        },
        changedUnit(value) {
            const sizes = ['mm', 'cm', 'in'];
            this.sizes.unit = sizes[value];
            this.unit = sizes[value];
        },
    },
}
</script>
